import { SectionKey, SectionKeyType } from './data-qa-constants';
import { DataQaDataset, FeatureMappingRow, NegativeValueFinding } from './data-qa-types';

// Each finding kind belongs to exactly one review section: feature-level media on
// Channels, the Portfolio totals on Portfolio, the target on Dependent Variable.
const SECTION_BY_FINDING_KIND: Record<NegativeValueFinding['kind'], SectionKeyType> = {
  media: SectionKey.CHANNELS,
  portfolio: SectionKey.PORTFOLIO,
  dependent: SectionKey.DEPENDENT_VAR
};

const BLOCKED_REASON_BY_KIND: Record<NegativeValueFinding['kind'], string> = {
  media:
    'A paid-media feature sums negative over a training period inside the model window. Models fail to train on this — fix the data before approving.',
  portfolio:
    'Total paid-media spend or impressions sums negative over a training period inside the model window. Fix the data before approving.',
  dependent:
    'The dependent variable sums negative over a training period inside the model window. Models fail to train on this — fix the data before approving.'
};

// Blocking = a review period inside the fencepost window sums negative, which is what
// training actually sees. Negative days that net out within their period don't block.
export const isBlockingFinding = (finding: NegativeValueFinding): boolean =>
  finding.negativePeriodsInWindow > 0;

export const findingsForSection = <T extends { finding: NegativeValueFinding }>(
  findings: T[],
  section: SectionKeyType
): T[] => findings.filter(({ finding }) => SECTION_BY_FINDING_KIND[finding.kind] === section);

// Each blocked section mapped to the reason its Approve button is disabled. Empty when
// the data frame is clean (or the load predates the check).
export const negativeValueBlockedSections = (
  dataset: DataQaDataset | undefined
): Map<SectionKeyType, string> => {
  const blocked = new Map<SectionKeyType, string>();
  (dataset?.negativeValues ?? []).filter(isBlockingFinding).forEach(finding => {
    blocked.set(SECTION_BY_FINDING_KIND[finding.kind], BLOCKED_REASON_BY_KIND[finding.kind]);
  });
  return blocked;
};

// "featid243__spend" -> "featid243__spend (Paid Social, Meta, None, None)" when the
// column's featid resolves to a mapped feature; the raw column name alone otherwise
// (dependent variables, Portfolio totals, Commerce-style self-describing media columns).
export const negativeValueColumnLabel = (
  column: string,
  featureMapping: FeatureMappingRow[]
): string => {
  const featid = /^(featid\d+)__/i.exec(column)?.[1]?.toUpperCase();
  if (!featid) return column;
  const row = featureMapping.find(r =>
    r.featureId
      .split(',')
      .map(id => id.trim().toUpperCase())
      .includes(featid)
  );
  return row ? `${column} (${row.feature})` : column;
};

// The model's training period, in words: 'weekly' -> "week". Anything unbucketed trains
// on each date directly, so its periods are days.
export const periodUnitFor = (reviewGranularity: string | undefined): string => {
  const granularity = (reviewGranularity ?? '').trim().toLowerCase();
  if (granularity === 'weekly') return 'week';
  if (granularity === 'monthly') return 'month';
  return 'day';
};

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;
const formatValue = (value: number) => value.toLocaleString('en-US');

// e.g. "2 weeks sum negative in the model window (lowest -1,250), plus 3 negative days;
// 2025-02-03 to 2025-02-10". On a daily model the period IS the day, so the day count
// isn't repeated.
export const describeNegativeFinding = (
  finding: NegativeValueFinding,
  periodUnit: string
): string => {
  const geo = finding.geoCount !== undefined;
  const period = geo ? `DMA-${periodUnit}` : periodUnit;
  const day = geo ? 'DMA-day' : 'day';
  const parts: string[] = [];
  const lowest =
    finding.minPeriodValue === undefined ? '' : ` (lowest ${formatValue(finding.minPeriodValue)})`;
  if (finding.negativePeriodsInWindow > 0) {
    parts.push(
      `${plural(finding.negativePeriodsInWindow, period)} sum${finding.negativePeriodsInWindow === 1 ? 's' : ''} negative in the model window${
        finding.negativePeriodsOutOfWindow
          ? `, ${finding.negativePeriodsOutOfWindow} more outside it`
          : ''
      }${lowest}`
    );
  } else if (finding.negativePeriodsOutOfWindow > 0) {
    parts.push(
      `${plural(finding.negativePeriodsOutOfWindow, period)} sum${finding.negativePeriodsOutOfWindow === 1 ? 's' : ''} negative outside the model window${lowest}`
    );
  }
  const periodsNegative = finding.negativePeriodsInWindow + finding.negativePeriodsOutOfWindow;
  if (periodUnit !== 'day' && finding.negativeRows > 0) {
    const days = `${plural(finding.negativeRows, `negative ${day}`)} (min ${formatValue(finding.minRowValue ?? 0)})`;
    parts.push(
      periodsNegative > 0 ? `plus ${days}` : `${days}, but every ${periodUnit} still sums positive`
    );
  }
  const range =
    finding.firstDate === finding.lastDate
      ? finding.firstDate
      : `${finding.firstDate} to ${finding.lastDate}`;
  const dmas = geo ? `, ${plural(finding.geoCount ?? 0, 'DMA')}` : '';
  return `${parts.join(', ')}; ${range}${dmas}`;
};
